import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import MobileHeader from '../../components/feature/MobileHeader';
import Select from '../../components/ui/Select';
import TimeSelector from '../../components/lottery/TimeSelector';
import MonthSelector from '../../components/lottery/MonthSelector';
import BilheteInput from '../../components/lottery/BilheteInput';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

export default function GerarApostas() {
  const [jogoSelecionado, setJogoSelecionado] = useState('mega-sena');
  const [quantidadeNumeros, setQuantidadeNumeros] = useState(6);
  const [quantidadeApostas, setQuantidadeApostas] = useState(1);
  const [metodo, setMetodo] = useState<'aleatorio' | 'balanceado' | 'frequencia' | 'atraso' | 'anti-repeticao' | 'manual' | 'premiei'>('aleatorio');
  const [vouJogar, setVouJogar] = useState(false);
  const [numerosSelecionados, setNumerosSelecionados] = useState<number[]>([]);
  const [apostasGeradas, setApostasGeradas] = useState<number[][]>([]);
  const [salvos, setSalvos] = useState<Record<number, boolean>>({});
  const [confete, setConfete] = useState<Record<number, boolean>>({});
  const location = useLocation();
  const megaVirada = Boolean((location.state as any)?.mega_virada);
  useEffect(() => {
    const presetLottery = (location.state as any)?.lottery;
    if (presetLottery) setJogoSelecionado(String(presetLottery));
  }, []);
  const [btnDims, setBtnDims] = useState<Record<number, { w: number; h: number }>>({});
  const btnRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const [premieiAnalysis, setPremieiAnalysis] = useState<any | null>(null);
  const [highlightTags, setHighlightTags] = useState<Record<number, 'good'|'neutral'|'bad'>>({});
  const [loterias, setLoterias] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [generationId, setGenerationId] = useState<number | null>(null);
  const [loadingLoterias, setLoadingLoterias] = useState(true);
  const [showSuperSete, setShowSuperSete] = useState(false);
  const [timeCoracao, setTimeCoracao] = useState<string>('');
  const [mesSorte, setMesSorte] = useState<string>('');
  const [bilheteFederal, setBilheteFederal] = useState<string>('');
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    carregarLoterias();
  }, []);

  useEffect(() => {
    // Quando mudar a loteria, ajustar a quantidade de números para o mínimo permitido
    const loteriaAtual = loterias.find(l => l.slug === jogoSelecionado);
    if (loteriaAtual) {
      const type = loteriaAtual.config?.type;
      if (type === 'dezenas') {
        setQuantidadeNumeros(loteriaAtual.config.min_numbers);
      } else if (type === 'bilhete') {
        setQuantidadeNumeros(5);
      } else if (type === 'colunas') {
        setQuantidadeNumeros(loteriaAtual.config.columns ?? 7);
      } else {
        setQuantidadeNumeros(6);
      }
      
      // Mostrar interface especial para Super Sete
      setShowSuperSete(jogoSelecionado === 'super-sete');
      
      // Limpar campos específicos ao mudar de loteria
      setTimeCoracao('');
      setMesSorte('');
      setBilheteFederal('');
      setNumerosSelecionados([]); // Limpar seleção ao mudar de loteria
      setApostasGeradas([]); // Limpar apostas geradas
      setGenerationId(null); // Limpar ID de geração
      setSalvos({});
      setConfete({});
      // Sugestão inteligente: Premiei na Mega-Sena recomenda 7–9 dezenas
      if (metodo === 'premiei' && jogoSelecionado === 'mega-sena' && quantidadeNumeros < 7) {
        setQuantidadeNumeros(7);
      }
    }
  }, [jogoSelecionado, loterias]);

  const carregarLoterias = async () => {
    try {
      setLoadingLoterias(true);
      const response = await apiService.getLotteries();
      setLoterias(response);
    } catch (error) {
      console.error('Erro ao carregar loterias:', error);
    } finally {
      setLoadingLoterias(false);
    }
  };

  const toggleNumero = (num: number) => {
    if (numerosSelecionados.includes(num)) {
      setNumerosSelecionados(numerosSelecionados.filter(n => n !== num));
    } else {
      if (numerosSelecionados.length < quantidadeNumeros) {
        setNumerosSelecionados([...numerosSelecionados, num]);
      }
    }
  };

  const limparSelecao = () => {
    setNumerosSelecionados([]);
  };

  const getNumeroOptions = () => {
    const loteriaAtual = loterias.find(l => l.slug === jogoSelecionado);
    if (!loteriaAtual) return [6, 7, 8, 9, 10, 15];
    
    const type = loteriaAtual.config?.type;
    if (type === 'dezenas') {
      const { min_numbers, max_numbers } = loteriaAtual.config;
      const options: number[] = [];
      for (let i = min_numbers; i <= max_numbers; i++) options.push(i);
      return options;
    }
    if (type === 'bilhete') {
      return [5];
    }
    if (type === 'colunas') {
      return [loteriaAtual.config.columns ?? 7];
    }
    return [6];
  };

  const gerarSurpresinha = () => {
    const loteriaAtual = loterias.find(l => l.slug === jogoSelecionado);
    const maxNumbers = loteriaAtual?.config?.range_max || 60;
    if (numerosSelecionados.length > 0 && numerosSelecionados.length < quantidadeNumeros) {
      const faltam = quantidadeNumeros - numerosSelecionados.length;
      const existentes = new Set(numerosSelecionados);
      const novos: number[] = [];
      while (novos.length < faltam) {
        const num = Math.floor(Math.random() * maxNumbers) + 1;
        if (!existentes.has(num) && !novos.includes(num)) novos.push(num);
      }
      const combinados = [...numerosSelecionados, ...novos].sort((a, b) => a - b);
      setNumerosSelecionados(combinados);
    } else {
      const numeros: number[] = [];
      while (numeros.length < quantidadeNumeros) {
        const num = Math.floor(Math.random() * maxNumbers) + 1;
        if (!numeros.includes(num)) {
          numeros.push(num);
        }
      }
      setNumerosSelecionados(numeros.sort((a, b) => a - b));
    }
  };

  const abrirVerificadorPremiei = async () => {
    const loteriaAtual = loterias.find(l => l.slug === jogoSelecionado);
    if (!loteriaAtual) return;
    const minNumbers = loteriaAtual.config?.min_numbers ?? quantidadeNumeros;
    if (numerosSelecionados.length !== minNumbers) {
      alert(`Selecione exatamente ${minNumbers} dezenas para analisar`);
      return;
    }
    try {
      const r = await apiService.verifyPremieiBet({
        lottery_slug: jogoSelecionado,
        selected_numbers: numerosSelecionados,
        numbers_count: numerosSelecionados.length,
      });
      setPremieiAnalysis(r);
      const tags: Record<number,'good'|'neutral'|'bad'> = {};
      (numerosSelecionados).forEach(n=>{
        const t = (r.tags && r.tags[n]) || 'neutral';
        tags[n] = t;
      });
      setHighlightTags(tags);
    } catch (e:any) {
      alert(apiService.extractErrorMessage(e));
    }
  };

  const limparAnalise = () => { setPremieiAnalysis(null); setHighlightTags({}); };

  const adicionarAposta = async () => {
    const loteriaAtual = loterias.find(l => l.slug === jogoSelecionado);
    const maxNumbers = loteriaAtual?.config?.range_max || 60;
    let aposta = [...numerosSelecionados];
    if (aposta.length < quantidadeNumeros) {
      const faltam = quantidadeNumeros - aposta.length;
      const existentes = new Set(aposta);
      const novos: number[] = [];
      while (novos.length < faltam) {
        const num = Math.floor(Math.random() * maxNumbers) + 1;
        if (!existentes.has(num) && !novos.includes(num)) novos.push(num);
      }
      aposta = [...aposta, ...novos].sort((a, b) => a - b);
    }
    if (aposta.length === quantidadeNumeros) {
      setApostasGeradas([...apostasGeradas, aposta]);
      setSalvos(prev => ({ ...prev, [apostasGeradas.length]: false }));
      setConfete(prev => ({ ...prev, [apostasGeradas.length]: false }));
      setNumerosSelecionados([]);

      // Criar geração para habilitar PDF automaticamente
      try {
        const response = await apiService.generateBets({
          lottery_slug: jogoSelecionado,
          method: 'manual',
          numbers_per_game: aposta.length,
          games_count: 1,
          manual_numbers: [aposta],
          vou_jogar: vouJogar
        });
        setGenerationId(response.generation_id);
      } catch (err) {
        console.error('Falha ao criar geração após adicionar aposta:', err);
      }
    }
  };

  const gerarPDF = async () => {
    if (!generationId) {
      alert('Gere as apostas primeiro antes de criar o PDF.');
      return;
    }

    setLoading(true);
    try {
      const pdfBlob = await apiService.getGenerationPdf(generationId);
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuperSeteClick = () => {
    navigate('/super-sete');
  };

  const gerarApostas = async () => {
    if (!user) {
      alert('Você precisa estar logado para gerar apostas.');
      return;
    }

    // Validações específicas por jogo
    if (jogoSelecionado === 'timemania' && !timeCoracao) {
      alert('Por favor, selecione o time do coração para Timemania.');
      return;
    }
    
    if (jogoSelecionado === 'dia-de-sorte' && !mesSorte) {
      alert('Por favor, selecione o mês da sorte para Dia de Sorte.');
      return;
    }
    
    if (jogoSelecionado === 'federal' && !bilheteFederal) {
      alert('Por favor, digite o número do bilhete para Loteria Federal.');
      return;
    }
    
    if (jogoSelecionado === 'federal' && bilheteFederal.length !== 5) {
      alert('O bilhete da Loteria Federal deve ter exatamente 5 dígitos.');
      return;
    }

    setLoading(true);
    try {
      const requestData: any = {
        lottery_slug: jogoSelecionado,
        method: metodo,
        numbers_per_game: quantidadeNumeros || getNumeroOptions()[0],
        games_count: quantidadeApostas,
        vou_jogar: vouJogar,
        manual_numbers: metodo === 'manual' && numerosSelecionados.length > 0 ? [numerosSelecionados] : undefined
      };

      // Adicionar campos extras específicos por jogo
      if (jogoSelecionado === 'timemania' && timeCoracao) {
        requestData.extra = {
          time_coracao: timeCoracao
        };
      }
      
      if (jogoSelecionado === 'dia-de-sorte' && mesSorte) {
        requestData.extra = {
          mes_sorte: mesSorte
        };
      }
      
      if (jogoSelecionado === 'federal' && bilheteFederal) {
        requestData.extra = {
          bilhete: bilheteFederal
        };
      }

      const response = await apiService.generateBets(requestData);
      setApostasGeradas(response.games);
      setGenerationId(response.generation_id);
      setSalvos({});
      setConfete({});
      
      if (response.ticket_id) {
        console.log('Ticket criado:', response.ticket_id);
      }
    } catch (error) {
      console.error('Erro ao gerar apostas:', error);
      alert('Erro ao gerar apostas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <Sidebar />
      <MobileHeader />
      
      <div className="md:ml-72 p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Gerar Apostas</h1>
          <p className="text-white/60 text-sm">Configure e gere suas apostas inteligentes</p>
        </div>

        {loadingLoterias ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-cyan-400 animate-spin mb-4"></i>
              <p className="text-white/60">Carregando loterias...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Configurações - Esquerda */}
            <div className="lg:col-span-1 space-y-6">
              {/* Jogo */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <label className="block text-sm font-bold mb-3">Jogo</label>
                <Select
                  value={jogoSelecionado}
                  onChange={(v) => setJogoSelecionado(String(v))}
                  options={loterias.map((loteria:any) => ({ value: loteria.slug, label: loteria.name }))}
                />
              </div>

              {/* Super Sete Button */}
              {jogoSelecionado === 'super-sete' && (
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <h3 className="text-lg font-bold mb-4">Interface Especial</h3>
                  <p className="text-white/60 text-sm mb-4">
                    Para apostar no Super Sete com a interface oficial, clique no botão abaixo:
                  </p>
                  <button
                    onClick={handleSuperSeteClick}
                    className="w-full px-6 py-4 text-base font-bold bg-gradient-to-r from-green-500 to-green-600 rounded-xl hover:shadow-2xl hover:shadow-green-500/40 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-clover-line mr-2"></i>
                    APOSTAR SUPER SETE
                  </button>
                </div>
              )}

              {/* Campos específicos por jogo */}
              {jogoSelecionado === 'timemania' && (
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <TimeSelector
                    onTimeChange={setTimeCoracao}
                    selectedTime={timeCoracao}
                  />
                </div>
              )}

              {jogoSelecionado === 'dia-de-sorte' && (
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <MonthSelector
                    onMonthChange={setMesSorte}
                    selectedMonth={mesSorte}
                  />
                </div>
              )}

              {jogoSelecionado === 'federal' && (
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <BilheteInput
                    onBilheteChange={setBilheteFederal}
                    bilhete={bilheteFederal}
                  />
                </div>
              )}

              {/* Quantidade de Números */}
              {!showSuperSete && (
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <label className="block text-sm font-bold mb-3">Quantidade de Números</label>
                  <Select
                    value={quantidadeNumeros}
                    onChange={(v) => setQuantidadeNumeros(Number(v))}
                    options={getNumeroOptions().map((num) => ({ value: num, label: `${num} ${num === 1 ? 'número' : 'números'}` }))}
                  />
                  {metodo === 'premiei' && jogoSelecionado === 'mega-sena' && (
                    <p className="mt-2 text-xs text-white/60">Sugestão Premiei: 7–9 dezenas para aumentar a chance matemática.</p>
                  )}
                </div>
              )}

              {/* Quantidade de Apostas */}
              {!showSuperSete && (
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <label className="block text-sm font-bold mb-3">Quantidade de Apostas</label>
                  <Select
                    value={quantidadeApostas}
                    onChange={(v) => setQuantidadeApostas(Number(v))}
                    options={[
                      { value: 1, label: '1 aposta' },
                      { value: 5, label: '5 apostas' },
                      { value: 10, label: '10 apostas' },
                      { value: 20, label: '20 apostas' }
                    ]}
                  />
                </div>
              )}

              {/* Método */}
              {!showSuperSete && (
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <label className="block text-sm font-bold mb-3">Método</label>
                  <div className="space-y-2">
                    {[
                      { value: 'aleatorio', label: 'Aleatório', hint: 'Números totalmente aleatórios', icon: 'ri-shuffle-line' },
                      { value: 'balanceado', label: 'Balanceado', hint: 'Equilibra pares e ímpares', icon: 'ri-scales-line' },
                      { value: 'frequencia', label: 'Mais sorteados (Frequência)', hint: 'Prioriza dezenas mais frequentes', icon: 'ri-bar-chart-line' },
                      { value: 'atraso', label: 'Mais atrasados', hint: 'Favorece dezenas que há tempos não saem', icon: 'ri-time-line' },
                      { value: 'anti-repeticao', label: 'Anti‑repetição', hint: 'Evita sequências e padrões comuns', icon: 'ri-refresh-line' },
                      { value: 'premiei', label: 'Premiei (Método Inteligente)', hint: 'Cruza frequência, atraso, correlação e balanceamento', icon: 'ri-brain-line' }
                    ].map((m) => (
                      <button
                        key={m.value}
                        onClick={() => setMetodo(m.value as any)}
                        className={`group w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-sm cursor-pointer ${
                          metodo === m.value
                            ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-white'
                            : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        <i className={m.icon}></i>
                        <span className="font-medium">{m.label}</span>
                        <i className="ri-information-line ml-auto text-white/50"></i>
                        <span className="pointer-events-none absolute translate-y-8 right-4 hidden group-hover:block bg-slate-800 text-xs text-white/80 border border-white/10 rounded-md px-2 py-1">
                          {m.hint}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Vou Jogar removido — ação por aposta */}

              {/* Botões */}
              {!showSuperSete && (
                <div className="space-y-3">
                  <button
                    onClick={gerarApostas}
                    disabled={loading}
                    className="w-full px-6 py-4 text-base font-bold bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl hover:shadow-2xl hover:shadow-cyan-500/40 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className={`${loading ? 'ri-loader-4-line animate-spin' : 'ri-magic-line'} mr-2`}></i>
                    {loading ? 'GERANDO...' : 'GERAR APOSTAS'}
                  </button>
                  <button 
                    onClick={gerarPDF}
                    disabled={loading || !generationId}
                    className="w-full px-6 py-4 text-base font-medium bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className={`${loading ? 'ri-loader-4-line animate-spin' : 'ri-file-pdf-line'} mr-2`}></i>
                    {loading ? 'GERANDO...' : 'GERAR PDF'}
                  </button>
                </div>
              )}
            </div>

            {/* Grid de Dezenas - Direita */}
            {!showSuperSete && (
              <div className="lg:col-span-2 space-y-6">
                {/* Volante */}
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold">Selecione os Números</h3>
                    <div className="text-sm">
                      <span className="text-white/60">Selecionados: </span>
                      <span className="font-bold text-cyan-400">{numerosSelecionados.length} / {quantidadeNumeros}</span>
                    </div>
                  </div>

                  {(() => {
                    const loteriaAtual = loterias.find((l) => l.slug === jogoSelecionado);
                    const maxNumbers = loteriaAtual?.config?.range_max ?? 60;
                    const cols = maxNumbers <= 25 ? 5 : 10;
                    return (
                      <div className={`grid gap-2 mb-6 ${cols === 5 ? 'grid-cols-5' : 'grid-cols-5 md:grid-cols-10'}`}>
                        {Array.from({ length: maxNumbers }, (_, i) => i + 1).map((num) => {
                          const selecionado = numerosSelecionados.includes(num);
                          const tag = selecionado ? highlightTags[num] : undefined;
                          return (
                            <button
                              key={num}
                              onClick={() => toggleNumero(num)}
                              className={`aspect-square flex items-center justify-center rounded-lg text-sm font-bold transition-all cursor-pointer ${
                                selecionado
                                  ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30 scale-105'
                                  : 'bg-white/5 border-2 border-dashed border-white/20 text-white/60 hover:bg-white/10 hover:border-white/40 hover:text-white'
                              } ${tag==='good' ? 'ring-2 ring-green-400' : tag==='bad' ? 'ring-2 ring-red-500 opacity-90' : ''}`}
                            >
                              {num.toString().padStart(2, '0')}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Surpresinha */}
                  <div className="bg-white/5 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className="text-sm font-medium text-white/80">Surpresinha:</span>
                      <button
                        onClick={gerarSurpresinha}
                        className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-sm font-bold hover:shadow-lg hover:shadow-purple-500/30 transition-all whitespace-nowrap cursor-pointer"
                      >
                        OK
                      </button>
                      <button
                        onClick={abrirVerificadorPremiei}
                        disabled={(() => { const lot = loterias.find(l=>l.slug===jogoSelecionado); const min = lot?.config?.min_numbers ?? quantidadeNumeros; return numerosSelecionados.length !== min; })()}
                        className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg text-sm font-bold hover:shadow-lg hover:shadow-emerald-500/30 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Verificador Premiei
                      </button>
                      <button
                        onClick={adicionarAposta}
                        disabled={numerosSelecionados.length === 0}
                        className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg text-sm font-bold hover:shadow-lg hover:shadow-cyan-500/30 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Adicionar aposta
                      </button>
                      <button
                        onClick={() => {
                          const loteriaAtual = loterias.find((l) => l.slug === jogoSelecionado);
                          const rangeMax = loteriaAtual?.config?.range_max ?? 60;
                          const need = (quantidadeNumeros || getNumeroOptions()[0]) - numerosSelecionados.length;
                          if (need <= 0) return;
                          const pool = Array.from({ length: rangeMax }, (_, i) => i + 1).filter(n => !numerosSelecionados.includes(n));
                          const pick: number[] = [];
                          let guard = 0;
                          while (pick.length < need && pool.length > 0 && guard < 500) {
                            const idx = Math.floor(Math.random() * pool.length);
                            const n = pool.splice(idx, 1)[0];
                            pick.push(n);
                            guard++;
                          }
                          setNumerosSelecionados([...numerosSelecionados, ...pick].sort((a,b)=>a-b));
                        }}
                        className="px-6 py-2 bg-white/5 border border-white/10 rounded-lg text-sm font-medium hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                      >
                        Completar
                      </button>
                      <button
                        onClick={limparSelecao}
                        className="px-6 py-2 bg-white/5 border border-white/10 rounded-lg text-sm font-medium hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                      >
                        Limpar
                      </button>
                    </div>
                  </div>
                </div>

                {/* Apostas Geradas */}
                {apostasGeradas.length > 0 && (
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                    <h3 className="text-xl font-bold mb-6">Apostas Geradas</h3>
                    <div className="space-y-4">
                      {apostasGeradas.map((aposta, idx) => (
                        <div key={idx} className="bg-white/5 rounded-xl p-4 relative overflow-visible">
                          {confete[idx] && (
                            <div className="confetti-container-top">
                              {Array.from({ length: 96 }).map((_, i) => {
                                const colors = ['#10B981','#22D3EE','#3B82F6','#F59E0B','#EF4444','#14B8A6'];
                                const color = colors[Math.floor(Math.random() * colors.length)];
                                const delay = Math.floor(Math.random() * 300);
                                const duration = 850 + Math.floor(Math.random() * 850);
                                const timings = ['ease-out','cubic-bezier(0.25, 0.85, 0.35, 1)','cubic-bezier(0.2, 0.8, 0.2, 1)'];
                                const timing = timings[Math.floor(Math.random() * timings.length)];
                                const x2 = (Math.random() - 0.5) * 40;
                                const w = 3 + Math.floor(Math.random() * 3);
                                const h = 6 + Math.floor(Math.random() * 4);
                                const left = Math.random() * 100;
                                const topStart = -Math.floor(Math.random() * 10);
                                return (
                                  <span
                                    key={i}
                                    className="confetti-piece confetti-piece--top"
                                    style={{
                                      left: `${left}%`,
                                      top: `${topStart}px`,
                                      backgroundColor: color,
                                      animationDelay: `${delay}ms`,
                                      animationDuration: `${duration}ms`,
                                      animationTimingFunction: timing,
                                      ['--dx2' as any]: `${x2}px`,
                                      width: `${w}px`,
                                      height: `${h}px`
                                    }}
                                  />
                                );
                              })}
                            </div>
                          )}
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-bold text-cyan-400">Aposta {idx + 1}</span>
                            <span className="text-xs text-white/60">{aposta.length} números</span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {aposta.map((num, numIdx) => (
                              <div
                                key={numIdx}
                                className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-lg text-sm font-bold"
                              >
                                {num.toString().padStart(2, '0')}
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 flex items-center justify-end relative">
                            <button
                              ref={(el) => { btnRefs.current[idx] = el; }}
                              onClick={async () => {
                                if (salvos[idx]) return;
                                try {
                                  let extra: any = undefined;
                                  if (jogoSelecionado === 'timemania' && timeCoracao) {
                                    extra = { time_coracao: timeCoracao };
                                  } else if (jogoSelecionado === 'dia-de-sorte' && mesSorte) {
                                    extra = { mes_sorte: mesSorte };
                                  } else if (jogoSelecionado === 'federal' && bilheteFederal) {
                                    extra = { bilhete: bilheteFederal };
                                  } else if (jogoSelecionado === 'mega-sena' && megaVirada) {
                                    extra = { mega_virada: true };
                                  }
                                  const res = await apiService.createGame(jogoSelecionado, aposta, extra);
                                  setSalvos({ ...salvos, [idx]: true });
                                  setConfete({ ...confete, [idx]: true });
                                  setTimeout(() => setConfete(prev => ({ ...prev, [idx]: false })), 1600);
                                } catch (e) {
                                  alert('Falha ao salvar jogo');
                                }
                              }}
                              className={`px-3 py-2 rounded-lg transition-all text-sm font-medium ${salvos[idx] ? 'bg-white/10 border border-white/20 text-white/60 animate-pop' : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:shadow-lg'}`}
                              disabled={!!salvos[idx]}
                            >
                              <i className={`${salvos[idx] ? 'ri-check-double-line' : 'ri-check-line'} mr-1`}></i>
                              {salvos[idx] ? 'Salvo!' : 'Vou jogar'}
                            </button>
                          </div>
                        </div>
                      ))}
                </div>
              </div>
            )}
          </div>
        )}
        {premieiAnalysis && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center">
            <div className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <span className="font-bold">Verificador Premiei</span>
                <div className="flex items-center gap-2">
                  <button onClick={()=>{ limparAnalise(); }} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10">Limpar análise</button>
                  <button onClick={()=> setPremieiAnalysis(null)} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10">Fechar</button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="text-3xl font-black">Score: {premieiAnalysis.overall_score}/100</div>
                  <span className={`px-3 py-1 text-xs font-bold rounded-full ${premieiAnalysis.recommendation==='Boa aposta' ? 'bg-green-500/20 text-green-400' : premieiAnalysis.recommendation==='Regular' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>{premieiAnalysis.recommendation}</span>
                </div>
                {premieiAnalysis.positives?.length>0 && (
                  <div>
                    <h4 className="font-bold mb-2">Pontos fortes</h4>
                    <ul className="list-disc list-inside text-sm text-white/80">
                      {premieiAnalysis.positives.map((p:string,idx:number)=>(<li key={idx}>{p}</li>))}
                    </ul>
                  </div>
                )}
                {premieiAnalysis.warnings?.length>0 && (
                  <div>
                    <h4 className="font-bold mb-2">Pontos a melhorar</h4>
                    <ul className="list-disc list-inside text-sm text-white/80">
                      {premieiAnalysis.warnings.map((p:string,idx:number)=>(<li key={idx}>{p}</li>))}
                    </ul>
                  </div>
                )}
                {premieiAnalysis.suggested_swaps?.length>0 && (
                  <div>
                    <h4 className="font-bold mb-2">Sugestões de troca</h4>
                    <ul className="list-disc list-inside text-sm text-white/80">
                      {premieiAnalysis.suggested_swaps.map((s:any,idx:number)=>(<li key={idx}>Considere trocar {String(s.from).padStart(2,'0')} por {String(s.to).padStart(2,'0')} ({s.reason})</li>))}
                    </ul>
                  </div>
                )}
                {premieiAnalysis.stats_snapshot && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-bold mb-2">Top frequentes</h4>
                      <div className="flex flex-wrap gap-2">
                        {premieiAnalysis.stats_snapshot.top_frequent.map((n:number,idx:number)=>(
                          <span key={idx} className="px-2 py-1 text-xs bg-white/10 border border-white/20 rounded-md">{String(n).padStart(2,'0')}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold mb-2">Top atrasados</h4>
                      <div className="flex flex-wrap gap-2">
                        {premieiAnalysis.stats_snapshot.top_delayed.map((n:number,idx:number)=>(
                          <span key={idx} className="px-2 py-1 text-xs bg-white/10 border border-white/20 rounded-md">{String(n).padStart(2,'0')}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
        )}
      </div>
    </div>
  );
}
